import { useState, useEffect } from 'react';
import { MeetingSessionConfiguration, VideoTileState, Logger, Attendee, VideoInputDevice } from 'amazon-chime-sdk-js';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import {
  useMeetingManager,
  useLocalVideo,
  useMeetingStatus,
  useToggleLocalMute,
  useContentShareState,
  useContentShareControls,
  useRosterState
} from 'amazon-chime-sdk-component-library-react';
import { Modal } from 'antd';
import { useNotification } from '../../contexts/NotificationContext/NotificationContext';
import { useRouter } from 'next/router';
import useMyRole from '@hooks/useMyRole';
import useChimeChangeBackground from '@hooks/video/useChimeChangeBackground';
import * as actions from '@actions';
import { Appointment } from 'vl-common/src/schemas/shared';

interface UserChimeIDs {
  clinician: string;
  patient: string;
}

export type Token = {
  Meeting: any;
  Attendee: Attendee;
};

interface ChimeLog {
  timestampMs: number;
  user_guid: string;
  appt_id: number;
  message: string;
}

interface ChimeVideoCall {
  leaveMeeting: (endCall?: () => void) => void;
  setBackgroundType: React.Dispatch<React.SetStateAction<string>>;
  supportedBackgrounds: Record<string, boolean>;
  muted: boolean;
  toggleMute: () => void;
  isVideoEnabled: boolean;
  toggleVideo: () => void;
  hasVideoDevices: boolean;
  hasAudioDevices: boolean;
  selectedVideoDevice: VideoInputDevice | undefined;
  isLocalUserSharing: boolean;
  toggleContentShare: () => void;
  userChimeIDs: UserChimeIDs;
  backgroundType: string;
}

function useChimeVideoCall({
  clinician_guid,
  patient_guid,
  token,
  logger,
  appointment,
  user_guid
}: {
  clinician_guid: string;
  patient_guid: string;
  token: Token;
  logger: Logger;
  appointment: Appointment;
  user_guid: string;
}): ChimeVideoCall {
  const dispatch = useDispatch();
  const { errorNotification } = useNotification();
  const meetingManager = useMeetingManager();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const meetingStatus = useMeetingStatus();
  const { muted, toggleMute } = useToggleLocalMute();
  const { roster } = useRosterState();
  const [userChimeIDs, setUserChimeIDs] = useState<UserChimeIDs>({ clinician: '', patient: '' });
  const { isLocalUserSharing } = useContentShareState();
  const { toggleContentShare } = useContentShareControls();
  const myRole = useMyRole();
  const router = useRouter();
  const {
    isBackgroundBlurSupported,
    isBackgroundReplacementSupported,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    backgroundType,
    setBackgroundType
  } = useChimeChangeBackground({ meetingManager, logger, user_guid, appointment });

  useEffect(() => {
    return () => {
      try {
        meetingManager?.audioVideo?.stop();
      } catch (e) {
        console.error(e);
      }
    };
  }, []);

  useEffect(() => {
    const rosterValues = Object.values(roster);
    const clinicianChimeID = rosterValues?.find((el) => el.externalUserId === clinician_guid)?.chimeAttendeeId;
    const patientChimeID = rosterValues?.find((el) => el.externalUserId === patient_guid)?.chimeAttendeeId;
    setUserChimeIDs({ clinician: clinicianChimeID || '', patient: patientChimeID || '' });
  }, [roster]);

  useEffect(() => {
    if (token?.Meeting && token?.Attendee) {
      join(token);
    }
  }, [token]);

  const sendChimeLog = async (body: ChimeLog) => {
    await dispatch(actions.sendChimeLog(body));
  };

  const debouncedSendChimeLog = debounce((body: ChimeLog) => sendChimeLog(body), 1000);

  const logAndShowMessage = async (description: string) => {
    errorNotification({
      message: 'An error occured',
      description
    });
    debouncedSendChimeLog({
      timestampMs: new Date().getTime(),
      user_guid,
      appt_id: appointment.appointment_id,
      message: description
    });
  };

  const permissionsCheck = async () => {
    const permissions = { audio: false, video: false };
    await navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then((stream) => {
        console.log('permissionsCheck succeeded', stream);
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      })
      .catch((err) => {
        console.log('permissionsCheck failed ', err);
      });
    await navigator.permissions
      // @ts-ignore
      .query({ name: 'microphone' })
      .then((permissionObj) => {
        permissions.audio = permissionObj.state === 'granted';
        if (permissionObj.state !== 'granted') {
          logAndShowMessage('No audio device found');
        }
      })
      .catch((error) => {
        console.log('Got error :', error);
      });
    await navigator.permissions
      // @ts-ignore
      .query({ name: 'camera' })
      .then((permissionObj) => {
        permissions.video = permissionObj.state === 'granted';
      })
      .catch((error) => {
        console.log('Got error :', error);
      });
    return permissions;
  };

  const startingDevices = (deviceStatus: { video: boolean; audio: boolean }) => {
    if (deviceStatus.audio && deviceStatus.video) return 4;
    if (deviceStatus.video) return 3;
    if (deviceStatus.audio) return 2;
    return 1;
  };

  const join = async (JoinInfo: any) => {
    console.log('joining Room');
    const perms = await permissionsCheck();
    console.log('perms', perms);
    if (JoinInfo?.Meeting && JoinInfo?.Attendee) {
      const meetingSessionConfiguration = new MeetingSessionConfiguration(JoinInfo.Meeting, JoinInfo.Attendee);
      try {
        await meetingManager
          .join(meetingSessionConfiguration, {
            deviceLabels: startingDevices(perms)
          })
          .catch((e) => {
            console.log('thisIsFailing', e);
            debouncedSendChimeLog({
              timestampMs: new Date().getTime(),
              user_guid,
              appt_id: appointment.appointment_id,
              message: `Failed to join meeting: ${e?.toString()}`
            });
          });
      } catch (e) {
        console.log('err', e);
      }
      const audioVideoManager = meetingManager?.audioVideo;
      const localVideoElement = document.getElementById('local_video');
      const remoteVideoElement = document.getElementById('remote_video');

      if (audioVideoManager) {
        let videoInputDevices;
        if (perms?.video) {
          try {
            videoInputDevices = await audioVideoManager
              ?.listVideoInputDevices(true)
              .catch((e) => console.log('123', e));
          } catch (e) {
            console.log('No video input devices', e);
            logAndShowMessage('No video input device found');
          }
        }

        let audioInputDevices;
        if (perms?.audio) {
          try {
            audioInputDevices = await audioVideoManager?.listAudioInputDevices(true);
          } catch (e) {
            console.log('No audio input devices', e);
            logAndShowMessage('No audio input device found');
          }
        }

        if (audioInputDevices?.[0]?.deviceId) {
          try {
            await audioVideoManager.startAudioInput(audioInputDevices?.[0]?.deviceId);
          } catch (e) {
            console.log('startAudioInput error:', e);
            debouncedSendChimeLog({
              timestampMs: new Date().getTime(),
              user_guid,
              appt_id: appointment.appointment_id,
              message: `Failed to start local audio: ${e?.toString()}`
            });
            logger.error(
              JSON.stringify({
                user_guid,
                appointment_id: appointment.appointment_id,
                event: 'USRLOG',
                message: `Failed to start local audio: ${e?.toString()}`
              })
            );
          }
        }
        if (videoInputDevices?.[0]?.deviceId) {
          try {
            await audioVideoManager.startVideoInput(videoInputDevices?.[0]?.deviceId);
          } catch (e) {
            console.log('startVideoInput error:', e);
            debouncedSendChimeLog({
              timestampMs: new Date().getTime(),
              user_guid,
              appt_id: appointment.appointment_id,
              message: `Failed to start local video: ${e?.toString()}`
            });
            logger.error(
              JSON.stringify({
                user_guid,
                appointment_id: appointment.appointment_id,
                event: 'USRLOG',
                message: `Failed to start local video: ${e?.toString()}`
              })
            );
          }
        }
        const observer = {
          // videoTileDidUpdate is called whenever a new tile is created or tileState changes.
          videoTileDidUpdate: (tileState: VideoTileState) => {
            // Ignore a tile without attendee ID and other attendee's tile.
            if (tileState.tileId) {
              if (!tileState.boundAttendeeId) {
                console.log('no attendee ID for tile');
                logger.info(
                  JSON.stringify({
                    user_guid,
                    appointment_id: appointment.appointment_id,
                    event: 'RECEIVING_VIDEO',
                    message: 'no attendee ID for tile'
                  })
                );
              } else if (tileState.localTile && localVideoElement instanceof HTMLVideoElement) {
                logger.info(
                  JSON.stringify({
                    user_guid,
                    appointment_id: appointment.appointment_id,
                    event: 'RECEIVING_VIDEO',
                    message: `Using local video as: ${tileState.boundAttendeeId}`
                  })
                );
                audioVideoManager.bindVideoElement(tileState.tileId, localVideoElement);
              } else if (tileState.isContent) {
                console.log('tileState?.boundExternalUserId', tileState?.boundExternalUserId);
                logger.info(
                  JSON.stringify({
                    user_guid,
                    appointment_id: appointment.appointment_id,
                    event: 'RECEIVING_VIDEO',
                    message: `Received screenshare from: ${tileState.boundAttendeeId}`
                  })
                );
                console.log('tileState.boundAttendeeId', tileState.boundAttendeeId);
                const screenshareVideoElement = document.getElementById(tileState.boundAttendeeId) as HTMLVideoElement;
                audioVideoManager.bindVideoElement(tileState.tileId, screenshareVideoElement);
              } else if (!tileState.localTile && remoteVideoElement instanceof HTMLVideoElement) {
                logger.info(
                  JSON.stringify({
                    user_guid,
                    appointment_id: appointment.appointment_id,
                    event: 'RECEIVING_VIDEO',
                    message: `Received remote video from: ${tileState.boundAttendeeId}`
                  })
                );
                audioVideoManager.bindVideoElement(tileState.tileId, remoteVideoElement);
              }
            } else {
              console.log('no tile ID');
            }
          }
        };
        audioVideoManager.startLocalVideoTile();
        audioVideoManager.addObserver(observer);
        await meetingManager.start().catch(async (e) => {
          logger.error(
            JSON.stringify({
              user_guid,
              appointment_id: appointment.appointment_id,
              event: 'USRLOG',
              message: `Failed to start meeting: ${e?.toString()}`
            })
          );
          await logAndShowMessage('Failed to start meeting. Please refresh.');
        });
      } else {
        console.log('no audio manager');
        debouncedSendChimeLog({
          timestampMs: new Date().getTime(),
          user_guid,
          appt_id: appointment.appointment_id,
          message: 'no audio manager'
        });
        logger.error(
          JSON.stringify({
            user_guid,
            appointment_id: appointment.appointment_id,
            event: 'USRLOG',
            message: 'no audio manager'
          })
        );
      }
    }
  };

  const handleHangUp = async (url?: string) => {
    if (meetingManager?.audioVideo) {
      meetingManager?.audioVideo?.stop();
      console.log('stopped audioVideo');
    }
    console.log('leaving meeting');
    await meetingManager.leave();
    try {
      debouncedSendChimeLog({
        timestampMs: new Date().getTime(),
        user_guid,
        appt_id: appointment.appointment_id,
        message: 'User hung up'
      });
    } catch (e) {
      console.error('Failed to send log message', e);
    }
    if (url) {
      router.replace('/patients').then();
    }
  };

  const leaveMeeting = (endCall?: () => void) => {
    if (myRole === 'patient') {
      handleHangUp('/patients');
    } else if (myRole === 'clinician' && endCall) {
      Modal.confirm({
        title: 'End Video Call',
        content: 'This will finish the video call which then cannot be restarted',
        onOk: () => {
          handleHangUp();
          endCall();
        }
      });
    } else {
      handleHangUp();
    }
  };

  const startVideo = async () => {
    await toggleVideo();
  };

  useEffect(() => {
    if (meetingStatus === 1) {
      startVideo();
    }
  }, [meetingStatus]);

  return {
    leaveMeeting,
    setBackgroundType,
    supportedBackgrounds: {
      none: true,
      blur: isBackgroundBlurSupported || false,
      HBS: isBackgroundReplacementSupported || false
    },
    muted,
    toggleMute,
    isVideoEnabled,
    toggleVideo,
    hasVideoDevices: videoDevices?.length > 0,
    hasAudioDevices: audioDevices?.length > 0,
    selectedVideoDevice,
    isLocalUserSharing,
    toggleContentShare,
    userChimeIDs,
    backgroundType
  };
}

export default useChimeVideoCall;
